import { useCallback, useState } from "react";

import {
  Refresh2 as RefreshIcon,
  Screen as FullScreenIcon,
  Image as ImageIcon,
  Expand as ExpandIcon,
  Minimize as MinimizeIcon,
} from "@hl/base-components/lib/assets/icons.generated";
import { GenArtUrlIFrame } from "@hl/shared-features/lib/features/gen-art/GenArtIFrame";
import genArtAspectRatio from "@hl/shared-features/lib/utils/aspectRatio";
import {
  ActionIcon,
  Box,
  Flex,
  Group,
  Modal,
  Tooltip,
  createStyles,
} from "@mantine/core";
import { useDisclosure, useFullscreen } from "@mantine/hooks";

import useMintState from "~hooks/useMintState";

type StyleProps = {
  isExpanded: boolean;
  aspectRatio?: string;
  isHover: boolean;
  showToolbarOnHover: boolean;
  responsive: boolean;
};
const useStyles = createStyles(
  (
    theme,
    {
      isExpanded,
      aspectRatio,
      isHover,
      showToolbarOnHover,
      responsive,
    }: StyleProps
  ) => {
    return {
      container: {
        aspectRatio,
        width: "100%",
        [theme.fn.largerThan("sm")]: {
          flex: 1,
          width: aspectRatio || isExpanded ? "auto" : "100%",
          maxWidth: "100%",
        },
        ...(responsive && {
          [theme.fn.largerThan("sm")]: {
            flex: 1,
            width: "100%",
            maxWidth: "100%",
            height: `calc(100% - 80px)`,
          },
          [theme.fn.smallerThan("sm")]: {
            width: "calc(100% - 32px)",
            height: "66.67vh",
          },
        }),
      },
      iframe: {
        height: aspectRatio && !responsive ? "auto" : "100%",
      },
      previewImage: {
        objectFit: "scale-down",
      },
      expanded: {
        height: responsive ? "100vh" : "calc(100vh - 30px)",
        overflow: "hidden",
      },
      closeBtn: {
        position: "absolute",
        top: 10,
        right: 10,
      },
      fullscreenButton: {
        [theme.fn.smallerThan("sm")]: {
          display: "none",
        },
      },
      toolbar: {
        marginTop: 32,
        boxShadow: theme.shadows.sm,
        borderRadius: 10,
        backgroundColor: theme.colors.baseBackground[0],
        ...(isExpanded && {
          position: "absolute",
          bottom: 20,
          marginBottom: 16,
        }),
        button: {
          height: 36,
          width: 36,
        },
        ...(showToolbarOnHover && {
          display: isHover ? "unset" : "none",
        }),
        ...(isExpanded &&
          responsive && {
            display: "none",
          }),
      },
      modal: {
        ...(responsive && {
          padding: `0px !important`,
        }),
      },
      modalBody: {
        maxHeight: "100%",
        ...(responsive && {
          padding: 0,
          margin: 0,
        }),
      },
    };
  }
);

type GenArtMintedViewProps = {
  animationUrl: string;
  imageUrl?: string | null;
  toolbarExtraClass?: string;
  showToolbarOnHover?: boolean;
};

export const GenArtMintedView = ({
  animationUrl,
  imageUrl,
  showToolbarOnHover = false,
  toolbarExtraClass,
}: GenArtMintedViewProps) => {
  const { collection } = useMintState();

  const aspectRatio = collection
    ? genArtAspectRatio(collection.generativeDetails?.captureSettings.viewPort)
    : undefined;

  const responsive = !!collection?.generativeDetails?.isResponsive;
  const enableRestrictiveFlags =
    !!collection?.flagVariations.enableIframeRestrictiveFlags;

  const iconSize = { width: 16, height: 16 };
  const [isExpanded, { open, close: closeModal }] = useDisclosure(false);
  const [isHover, setHover] = useState(false);
  const { classes, cx } = useStyles({
    isExpanded,
    aspectRatio,
    isHover,
    showToolbarOnHover,
    responsive,
  });
  const { ref: iframeRef, toggle: toggleFullscreen } = useFullscreen();
  const [ver, setVer] = useState(1);
  const [isShowingImage, setShowingImage] = useState(false);

  const showImage = useCallback(() => {
    open();
    setShowingImage(true);
  }, [open]);

  const close = useCallback(() => {
    closeModal();
    setShowingImage(false);
  }, [closeModal]);

  const refreshIframe = useCallback(() => {
    setVer((ver) => ver + 1);
  }, []);

  const content = (
    <Flex
      pos="relative"
      direction="column"
      w="100%"
      h={isExpanded ? "100vh" : "100%"}
      align="center"
      justify="center"
    >
      <Box
        className={classes.container}
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <GenArtUrlIFrame
          url={animationUrl + `&ver=${ver}`}
          aspectRatio={aspectRatio}
          iframeRef={iframeRef}
          bordered={false}
          extraClass={classes.iframe}
          enableRestrictiveFlags={enableRestrictiveFlags}
        />
      </Box>
      <Box
        p={4}
        className={cx(classes.toolbar, toolbarExtraClass)}
        w="auto"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
      >
        <Group spacing={4}>
          <Tooltip label="Refresh">
            <ActionIcon onClick={refreshIframe}>
              <RefreshIcon {...iconSize} />
            </ActionIcon>
          </Tooltip>

          {!isExpanded && (
            <Tooltip label="Expand">
              <ActionIcon onClick={open}>
                <ExpandIcon {...iconSize} />
              </ActionIcon>
            </Tooltip>
          )}

          <Tooltip label="Fullscreen" className={classes.fullscreenButton}>
            <ActionIcon onClick={toggleFullscreen}>
              <FullScreenIcon {...iconSize} />
            </ActionIcon>
          </Tooltip>
          {imageUrl && (
            <Tooltip label="Preview image">
              <ActionIcon onClick={showImage}>
                <ImageIcon {...iconSize} />
              </ActionIcon>
            </Tooltip>
          )}
        </Group>
      </Box>
    </Flex>
  );
  if (isExpanded) {
    return (
      <Modal
        opened={isExpanded}
        onClose={close}
        fullScreen
        withCloseButton={false}
        centered
        classNames={{
          modal: classes.modal,
          body: classes.modalBody,
        }}
      >
        <div className={classes.expanded}>
          {isShowingImage && imageUrl ? (
            <Flex justify="center" align="center" pos="relative" h="100%">
              <img
                className={cx([classes.container, classes.previewImage])}
                src={imageUrl}
              />
            </Flex>
          ) : (
            content
          )}
        </div>
        <ActionIcon className={classes.closeBtn} onClick={close}>
          <MinimizeIcon />
        </ActionIcon>
      </Modal>
    );
  }

  return content;
};
